import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft,
  Tag,
  Truck,
  CreditCard,
  Gift,
  ShieldCheck,
  Sparkles,
  Lock,
  Check,
  CheckCircle2,
  BookmarkCheck,
  Plane,
  Zap,
  Clock,
  ChevronDown,
  ChevronUp,
  MapPin,
  PackageCheck,
  Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import API from '../config/api';
import { processRazorpayPayment } from '../lib/razorpay';
import { trackMetaEvent, getMetaContext } from '../lib/meta';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutTracked = useRef(false);

  useEffect(() => {
    if (checkoutTracked.current || authLoading || !user || cart.length === 0) return;
    checkoutTracked.current = true;
    trackMetaEvent('InitiateCheckout', {
      currency: 'INR',
      value: cartTotal,
      content_type: 'product',
      content_ids: cart.map(item => String(item.id)),
      num_items: cart.reduce((sum, item) => sum + item.quantity, 0)
    });
  }, [authLoading, user, cart, cartTotal]);

  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' (Razorpay) or 'cod'
  
  // Coupon state
  const initialCoupon = location.state?.appliedCoupon || null;
  const [couponCode, setCouponCode] = useState(initialCoupon?.code || '');
  const [couponApplied, setCouponApplied] = useState(initialCoupon);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showCouponsList, setShowCouponsList] = useState(false);

  // Shiprocket Shipping & Package Selection State
  const [shippingPackages, setShippingPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkingShipping, setCheckingShipping] = useState(false);
  const [shippingServiceable, setShippingServiceable] = useState(true);
  const [shippingMessage, setShippingMessage] = useState('');
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [showCourierDetails, setShowCourierDetails] = useState(false);

  // Gift state
  const [isGift, setIsGift] = useState(false);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftRecipientName, setGiftRecipientName] = useState('');
  const [giftSenderName, setGiftSenderName] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [giftHidePrice, setGiftHidePrice] = useState(true);

  // Saved address auto-fill indicator
  const [hasLoadedSavedAddress, setHasLoadedSavedAddress] = useState(false);

  // Shipping Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'Uttarakhand',
    pincode: ''
  });

  // Pre-fill user data from Database Profile and LocalStorage
  useEffect(() => {
    let savedLocal = {};
    try {
      const cached = localStorage.getItem('paramparik_saved_address');
      if (cached) savedLocal = JSON.parse(cached);
    } catch (e) {
      // ignore
    }

    if (user) {
      const name = user.name || savedLocal.name || '';
      const email = user.email || savedLocal.email || '';
      const phone = user.phone || savedLocal.phone || '';
      const address = user.address || savedLocal.address || '';
      const city = user.city || savedLocal.city || '';
      const state = user.state || savedLocal.state || 'Uttarakhand';
      const pincode = user.pincode || savedLocal.pincode || '249401';

      setFormData({
        name,
        email,
        phone,
        address,
        city,
        state,
        pincode
      });

      setGiftSenderName(name);

      if (phone && address && city && pincode) {
        setHasLoadedSavedAddress(true);
      }
    } else if (savedLocal.phone || savedLocal.address) {
      setFormData(prev => ({
        ...prev,
        ...savedLocal
      }));
      setHasLoadedSavedAddress(true);
    }
  }, [user]);

  // Auth & Cart Check
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please login to continue with your checkout');
      navigate('/login?redirect=/checkout');
    }
    if (cart.length === 0 && !submitting) {
      navigate('/cart');
    }
  }, [user, authLoading, cart.length, navigate, submitting]);

  // Load available coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await axios.get(`${API}/coupons/active`);
        if (Array.isArray(res.data)) {
          setAvailableCoupons(res.data);
        }
      } catch (err) {
        console.error('Failed to load coupons:', err);
      }
    };
    fetchCoupons();
  }, []);

  // Fetch live Shiprocket courier packages and rates when PIN code is valid
  useEffect(() => {
    const fetchShippingPackages = async () => {
      const cleanPin = String(formData.pincode || '').trim();
      if (cleanPin.length !== 6) {
        return;
      }

      setCheckingShipping(true);
      try {
        const res = await axios.post(`${API}/shipping/serviceability`, {
          pincode: cleanPin,
          weight: 0.5,
          subtotal: cartTotal,
          cod: paymentMethod === 'cod' ? 1 : 0
        });

        if (res.data && res.data.packages && res.data.packages.length > 0) {
          setShippingPackages(res.data.packages);
          setShippingServiceable(res.data.serviceable !== false);
          setShippingMessage(res.data.message || '');
          if (res.data.couriers) {
            setAvailableCouriers(res.data.couriers);
          }

          // Auto-select best matching package or preserve current choice
          setSelectedPackage(prev => {
            if (!prev) return res.data.packages[0];
            const match = res.data.packages.find(p => p.id === prev.id);
            return match || res.data.packages[0];
          });
        }
      } catch (err) {
        console.error('Failed to fetch shipping packages:', err);
      } finally {
        setCheckingShipping(false);
      }
    };

    fetchShippingPackages();
  }, [formData.pincode, cartTotal, paymentMethod]);

  const handleApplyCoupon = async (codeOverride) => {
    const code = (codeOverride || couponCode).trim();
    if (!code) return;
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code,
        cart_total: cartTotal
      });
      setCouponApplied(res.data);
      setCouponCode(res.data.code);
      toast.success(res.data.message || `Coupon ${res.data.code} applied!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon');
      setCouponApplied(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleGiftTemplate = (text) => {
    setGiftMessage(text);
  };

  const giftMessageTemplates = [
    'May the divine blessings of Lord Shiva bring peace, health, and prosperity to you and your home.',
    'A sacred energized offering from the Himalayan shrines for your well-being.',
    'Wishing you happiness, joy, and divine harmony.'
  ];

  // Calculations
  const shippingCost = selectedPackage ? selectedPackage.rate : (cartTotal >= 999 ? 0 : 49);
  const discountAmount = couponApplied?.discount || 0;
  const giftWrapFee = (isGift && giftWrap) ? 49 : 0;
  const finalPayableTotal = Math.max(0, cartTotal - discountAmount + shippingCost + giftWrapFee);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.address || !formData.city || !formData.pincode) {
      toast.error('Please fill in all required delivery address fields');
      return;
    }

    if (formData.phone.length < 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    // Save to local storage for instant future pre-fill
    try {
      localStorage.setItem('paramparik_saved_address', JSON.stringify(formData));
    } catch (err) {
      // ignore
    }

    setSubmitting(true);

    try {
      const orderPayload = {
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          addons: item.selectedAddons || []
        })),
        shipping_address: {
          name: formData.name,
          email: formData.email || user.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        },
        payment_method: paymentMethod,
        coupon_code: couponApplied?.code || null,
        shipping_package: selectedPackage || {
          id: 'pkg_standard_surface',
          name: 'Standard Sacred Delivery',
          courier_name: 'Delhivery Surface / Express',
          rate: shippingCost,
          mode: 'Surface',
          etd: '3-5 business days'
        },
        shipping_cost: shippingCost,
        package_weight_kg: 0.5,
        is_gift: isGift,
        gift_wrap: isGift && giftWrap,
        gift_recipient_name: isGift ? giftRecipientName : null,
        gift_sender_name: isGift ? giftSenderName : null,
        gift_message: isGift ? giftMessage : null,
        gift_hide_price: isGift ? giftHidePrice : false,
        meta: getMetaContext()
      };

      // 1. Create order on backend (also auto-persists address to User in DB)
      const response = await axios.post(`${API}/orders`, orderPayload, { withCredentials: true });
      const createdOrder = response.data;

      // 2. Handle Payment Method
      if (paymentMethod === 'cod') {
        clearCart();
        toast.success('Order placed successfully! Pay on delivery.');
        navigate(`/orders/${createdOrder.order_id}?success=true`);
      } else {
        // 3. Razorpay Checkout
        toast.info('Opening secure Razorpay gateway...');
        
        await processRazorpayPayment({
          orderId: createdOrder.order_id,
          orderType: 'order',
          user: {
            name: formData.name,
            email: formData.email || user.email,
            phone: formData.phone
          },
          onSuccess: () => {
            clearCart();
            toast.success('Payment verified successfully!');
            navigate(`/orders/${createdOrder.order_id}?success=true`);
          },
          onError: (errMsg) => {
            toast.error(errMsg || 'Payment failed or cancelled');
            setSubmitting(false);
            navigate(`/payment/${createdOrder.order_id}?type=order`);
          },
          onDismiss: () => {
            setSubmitting(false);
            toast.info('Payment window closed. You can complete it from the payment page.');
            navigate(`/payment/${createdOrder.order_id}?type=order`);
          }
        });
      }
    } catch (error) {
      console.error('Order creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#6E1B34] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF7F0] min-h-screen flex flex-col text-[#2A1F16]">
      <Navbar />

      <main className="flex-1 pt-20 sm:pt-24 pb-12 relative overflow-hidden">
        {/* Ambient Glow */}
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#E8D5A8]/20 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
        <div className="absolute bottom-10 right-10 w-[25rem] h-[25rem] bg-[#6E1B34]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/cart')}
              className="inline-flex items-center space-x-1.5 text-[#83725B] hover:text-[#4E1225] mb-2.5 transition-colors text-xs font-semibold uppercase tracking-wider bg-white/80 px-3 py-1.5 rounded-full border border-[#E7D9BC] shadow-2xs group"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Cart</span>
            </button>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h1 className="font-serif-heading text-2xl sm:text-3xl font-bold text-[#4E1225] tracking-tight">
                Checkout & <span className="italic text-[#6E1B34]">Delivery Details</span>
              </h1>
              <span className="text-[11px] font-semibold text-[#83725B] bg-white/90 px-3 py-1 rounded-full border border-[#E7D9BC]">
                Logged in as <strong>{user?.name}</strong>
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6 items-start">
            
            {/* Left Column: Delivery Form, Package Choice, Gifting, Payment Choice */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-4">
              
              <form onSubmit={handleSubmit} id="checkout-form" className="space-y-4">
                
                {/* 1. Delivery Address Card */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xs border border-[#E7D9BC]">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E7D9BC]/60">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-[#6E1B34] text-white flex items-center justify-center font-bold text-xs">
                        1
                      </div>
                      <h2 className="font-serif-heading text-base sm:text-lg font-bold text-[#4E1225]">
                        Delivery Address
                      </h2>
                    </div>

                    {hasLoadedSavedAddress && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2E7D45] bg-[#E8F5E9] border border-[#C8E6C9] px-2 py-0.5 rounded-full">
                        <BookmarkCheck className="w-3 h-3" />
                        <span>Saved Address Loaded</span>
                      </span>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="name" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Devotee Name"
                          className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30"
                          required
                          data-testid="checkout-name"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="phone" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                          Mobile Number *
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="10-digit mobile number"
                          className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30"
                          required
                          data-testid="checkout-phone"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="email" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                        Email Address (for invoice & tracking updates) *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@example.com"
                        className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30"
                        required
                        data-testid="checkout-email"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="address" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                        Street Address / Flat / House No. / Landmark *
                      </Label>
                      <textarea
                        id="address"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="House No., Street, Colony, Landmark"
                        className="w-full rounded-lg border border-[#E7D9BC] px-3 py-1.5 text-xs sm:text-sm bg-[#FAF7F0]/30 focus:outline-none focus:ring-1 focus:ring-[#6E1B34] focus:border-[#6E1B34]"
                        required
                        data-testid="checkout-address"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="pincode" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                          PIN Code *
                        </Label>
                        <Input
                          id="pincode"
                          value={formData.pincode}
                          maxLength={6}
                          onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '') })}
                          placeholder="6-digit PIN"
                          className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30 font-mono font-bold"
                          required
                          data-testid="checkout-pincode"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="city" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                          City / District *
                        </Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="e.g. Haridwar"
                          className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30"
                          required
                          data-testid="checkout-city"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="state" className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                          State *
                        </Label>
                        <Input
                          id="state"
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          placeholder="e.g. Uttarakhand"
                          className="h-9 rounded-lg border-[#E7D9BC] text-xs sm:text-sm bg-[#FAF7F0]/30"
                          required
                          data-testid="checkout-state"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Choose Delivery Package (Shiprocket Integration) */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xs border border-[#E7D9BC]">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E7D9BC]/60 flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-[#6E1B34] text-white flex items-center justify-center font-bold text-xs">
                        2
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="font-serif-heading text-base sm:text-lg font-bold text-[#4E1225]">
                            Choose Delivery Package
                          </h2>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] px-2 py-0.5 rounded-full">
                            <Truck className="w-3 h-3 text-[#D9711F]" />
                            <span>Powered by Shiprocket</span>
                          </span>
                        </div>
                        <p className="text-[11px] text-[#83725B]">
                          Select your preferred courier speed & package handling
                        </p>
                      </div>
                    </div>

                    {formData.pincode?.length === 6 && (
                      <span className="text-[11px] font-semibold text-[#2E7D45] bg-[#E8F5E9] border border-[#C8E6C9] px-2.5 py-0.5 rounded-full inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>PIN: {formData.pincode}</span>
                      </span>
                    )}
                  </div>

                  {checkingShipping ? (
                    <div className="py-8 text-center space-y-2">
                      <div className="w-7 h-7 border-2 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-xs text-[#83725B] font-medium">
                        Checking Shiprocket courier rates & delivery packages for {formData.pincode || 'your pincode'}...
                      </p>
                    </div>
                  ) : shippingPackages.length > 0 ? (
                    <div className="space-y-3">
                      {/* Package Cards Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {shippingPackages.map((pkg) => {
                          const isSelected = selectedPackage?.id === pkg.id;
                          return (
                            <div
                              key={pkg.id}
                              onClick={() => setSelectedPackage(pkg)}
                              className={`relative cursor-pointer rounded-xl p-3.5 border-2 transition-all flex flex-col justify-between ${
                                isSelected
                                  ? 'border-[#6E1B34] bg-[#FBF4E7]/90 shadow-sm ring-1 ring-[#6E1B34]'
                                  : 'border-[#E7D9BC] hover:border-[#B98B3E] bg-white/70 hover:bg-[#FAF7F0]/60'
                              }`}
                              data-testid={`package-option-${pkg.id}`}
                            >
                              {/* Top Badge */}
                              <div className="flex items-center justify-between gap-1 mb-2">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                                  pkg.badge === 'Fastest'
                                    ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                    : pkg.badge === 'Most Popular'
                                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                    : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                }`}>
                                  {pkg.badge || 'Standard'}
                                </span>

                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                  isSelected ? 'border-[#6E1B34] bg-[#6E1B34]' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                              </div>

                              {/* Title & Courier */}
                              <div>
                                <h3 className="font-serif-heading font-bold text-xs sm:text-sm text-[#4E1225]">
                                  {pkg.name}
                                </h3>
                                <p className="text-[11px] text-[#83725B] font-medium flex items-center gap-1 mt-0.5">
                                  <Truck className="w-3 h-3 text-[#D9711F]" />
                                  <span>{pkg.courier_name}</span>
                                </p>
                              </div>

                              {/* ETD & Estimate */}
                              <div className="my-2.5 py-2 border-y border-[#E7D9BC]/50 text-[11px] space-y-0.5">
                                <div className="flex items-center gap-1 text-[#4E1225] font-semibold">
                                  <Clock className="w-3 h-3 text-[#6E1B34]" />
                                  <span>{pkg.etd}</span>
                                </div>
                                {pkg.estimated_delivery_date && (
                                  <p className="text-[10px] text-[#2E7D45] font-bold">
                                    Arrives by {pkg.estimated_delivery_date}
                                  </p>
                                )}
                              </div>

                              {/* Price */}
                              <div className="flex items-baseline justify-between pt-1">
                                <span className="text-[10px] text-[#83725B] uppercase font-bold">
                                  Delivery Fee:
                                </span>
                                <span className="font-price font-bold text-sm sm:text-base text-[#6E1B34]">
                                  {pkg.rate === 0 ? (
                                    <span className="text-[#2E7D45] font-bold">FREE</span>
                                  ) : (
                                    `₹${pkg.rate}`
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Supported Couriers Dropdown Preview */}
                      {availableCouriers.length > 0 && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setShowCourierDetails(!showCourierDetails)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#6E1B34] hover:text-[#4E1225] bg-[#FBF4E7] px-3 py-1.5 rounded-lg border border-[#E7D9BC]"
                          >
                            <Building className="w-3.5 h-3.5 text-[#D9711F]" />
                            <span>
                              {showCourierDetails ? 'Hide' : 'View'} all {availableCouriers.length} Shiprocket courier partners for PIN {formData.pincode}
                            </span>
                            {showCourierDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>

                          {showCourierDetails && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              className="mt-2.5 p-3 bg-[#FAF7F0] border border-[#E7D9BC] rounded-xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs"
                            >
                              {availableCouriers.map((c, idx) => (
                                <div key={idx} className="bg-white p-2.5 rounded-lg border border-[#E7D9BC]/60 flex items-center justify-between">
                                  <div>
                                    <p className="font-bold text-[#4E1225] text-[11px]">{c.courier_name}</p>
                                    <p className="text-[10px] text-[#83725B]">{c.etd} • Rating: ⭐ {c.rating || 4.5}</p>
                                  </div>
                                  <span className="text-[11px] font-bold text-[#6E1B34]">
                                    ₹{c.rate}
                                  </span>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-[#FAF7F0] border border-[#E7D9BC] rounded-xl text-center text-xs text-[#83725B]">
                      <Truck className="w-6 h-6 text-[#D9711F] mx-auto mb-1.5" />
                      <p className="font-semibold text-[#4E1225]">Enter your 6-digit delivery PIN code above</p>
                      <p className="text-[11px]">We will automatically fetch available Shiprocket courier packages and delivery dates for your location.</p>
                    </div>
                  )}
                </div>

                {/* 3. Gift Options Card */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xs border border-[#E7D9BC]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] flex items-center justify-center font-bold text-xs">
                        3
                      </div>
                      <div>
                        <h2 className="font-serif-heading text-base sm:text-lg font-bold text-[#4E1225]">
                          Sacred Gift Options
                        </h2>
                        <p className="text-[11px] text-[#83725B]">Send divine offerings directly to loved ones</p>
                      </div>
                    </div>
                    
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isGift}
                        onChange={(e) => setIsGift(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#6E1B34]"></div>
                    </label>
                  </div>

                  <AnimatePresence>
                    {isGift && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3 pt-3 border-t border-[#E7D9BC]/60 overflow-hidden"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                              Recipient Name (To)
                            </Label>
                            <Input
                              value={giftRecipientName}
                              onChange={(e) => setGiftRecipientName(e.target.value)}
                              placeholder="e.g. Sharma Family, Friend"
                              className="h-9 rounded-lg text-xs sm:text-sm border-[#E7D9BC] bg-[#FBF4E7]/40"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                              Sender Name (From)
                            </Label>
                            <Input
                              value={giftSenderName}
                              onChange={(e) => setGiftSenderName(e.target.value)}
                              placeholder="Your Name"
                              className="h-9 rounded-lg text-xs sm:text-sm border-[#E7D9BC] bg-[#FBF4E7]/40"
                            />
                          </div>
                        </div>

                        {/* Sacred Message */}
                        <div className="space-y-1">
                          <Label className="text-[11px] font-bold text-[#4E1225] uppercase tracking-wider">
                            Personalized Blessing Message
                          </Label>
                          <textarea
                            rows={2}
                            value={giftMessage}
                            onChange={(e) => setGiftMessage(e.target.value)}
                            placeholder="Write your blessing or wishes here..."
                            className="w-full rounded-lg border border-[#E7D9BC] px-3 py-1.5 text-xs sm:text-sm bg-[#FBF4E7]/30 focus:outline-none focus:ring-1 focus:ring-[#6E1B34]"
                          />
                          <div className="flex flex-wrap gap-1 pt-0.5">
                            <span className="text-[10px] text-[#83725B] font-medium mr-1">Quick Templates:</span>
                            {giftMessageTemplates.map((template, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleGiftTemplate(template)}
                                className="text-[10px] bg-[#FBF4E7] text-[#6E1B34] border border-[#E7D9BC] rounded px-1.5 py-0.5 hover:bg-[#F3E7D0] transition-colors"
                              >
                                Blessing {idx + 1}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Gift Wrap & Hide Price */}
                        <div className="pt-1 space-y-2 text-xs">
                          <label className="flex items-start gap-2.5 p-2.5 rounded-xl border border-[#E7D9BC] bg-[#FBF4E7] cursor-pointer hover:bg-[#F3E7D0]/60 transition-colors">
                            <input
                              type="checkbox"
                              checked={giftWrap}
                              onChange={(e) => setGiftWrap(e.target.checked)}
                              className="mt-0.5 h-3.5 w-3.5 text-[#6E1B34] rounded border-gray-300 focus:ring-[#6E1B34]"
                            />
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-[#4E1225]">
                                  Himalayan Sacred Packaging (+₹49)
                                </span>
                                <span className="text-[10px] bg-[#F3E7D0] text-[#B85A15] font-bold px-1.5 py-0.2 rounded border border-[#E7D9BC]">
                                  ✨ Sacred Touch
                                </span>
                              </div>
                              <p className="text-[11px] text-[#83725B]">
                                Auspicious packaging with Mauli thread & handwritten blessings card.
                              </p>
                            </div>
                          </label>

                          <label className="flex items-center gap-2 px-1 cursor-pointer text-[#83725B]">
                            <input
                              type="checkbox"
                              checked={giftHidePrice}
                              onChange={(e) => setGiftHidePrice(e.target.checked)}
                              className="h-3.5 w-3.5 text-[#6E1B34] rounded border-gray-300 focus:ring-[#6E1B34]"
                            />
                            <span className="text-[11px]">
                              Hide product prices on physical delivery invoice (Gift Receipt)
                            </span>
                          </label>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* 4. Payment Method Choice */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xs border border-[#E7D9BC]">
                  <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-[#E7D9BC]/60">
                    <div className="w-7 h-7 rounded-lg bg-[#6E1B34] text-white flex items-center justify-center font-bold text-xs">
                      4
                    </div>
                    <div>
                      <h2 className="font-serif-heading text-base sm:text-lg font-bold text-[#4E1225]">
                        Payment Method
                      </h2>
                      <p className="text-[11px] text-[#83725B]">Protected & encrypted 256-bit checkout</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Razorpay Online */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('online')}
                      className={`flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                        paymentMethod === 'online'
                          ? 'border-[#6E1B34] bg-[#FBF4E7] shadow-2xs'
                          : 'border-[#E7D9BC] hover:border-[#B98B3E] bg-white'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${paymentMethod === 'online' ? 'bg-[#6E1B34] text-white' : 'bg-gray-100 text-gray-400'}`}>
                        <CreditCard className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-[#4E1225] text-xs sm:text-sm">Pay Online</p>
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                            Instant
                          </span>
                        </div>
                        <p className="text-[11px] text-[#83725B] mt-0.5">
                          UPI, GPay, PhonePe, Cards, NetBanking via Razorpay
                        </p>
                      </div>
                    </button>

                    {/* Cash on Delivery */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('cod')}
                      className={`flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                        paymentMethod === 'cod'
                          ? 'border-[#6E1B34] bg-[#FBF4E7] shadow-2xs'
                          : 'border-[#E7D9BC] hover:border-[#B98B3E] bg-white'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${paymentMethod === 'cod' ? 'bg-[#6E1B34] text-white' : 'bg-gray-100 text-gray-400'}`}>
                        <Truck className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#4E1225] text-xs sm:text-sm">Cash on Delivery</p>
                        <p className="text-[11px] text-[#83725B] mt-0.5">
                          Pay cash or UPI upon package arrival
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

              </form>

            </div>

            {/* Right Column: Order Summary & Pay */}
            <div className="lg:col-span-5 xl:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl p-5 shadow-xs border border-[#E7D9BC] sticky top-24 space-y-4"
              >
                <div className="flex items-center justify-between pb-3 border-b border-[#E7D9BC]/50">
                  <h3 className="font-serif-heading text-lg font-bold text-[#4E1225]">
                    Order Summary
                  </h3>
                  <span className="text-xs text-[#83725B] font-semibold bg-[#FBF4E7] px-2 py-0.5 rounded-full border border-[#E7D9BC]">
                    {cart.length} {cart.length === 1 ? 'Item' : 'Items'}
                  </span>
                </div>

                {/* Items Preview */}
                <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                  {cart.map((item) => {
                    const lineKey = item.cartItemId || item.id;
                    return (
                      <div key={lineKey} className="flex items-center gap-2.5">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-10 h-10 rounded-lg object-cover border border-[#E7D9BC] flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-[#4E1225] truncate">{item.name}</p>
                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <p className="text-[10px] text-[#6E1B34] font-medium truncate">
                              + {item.selectedAddons.map(a => a.name).join(', ')}
                            </p>
                          )}
                          <p className="text-[11px] text-[#83725B]">Qty: {item.quantity} &times; ₹{item.price.toLocaleString()}</p>
                        </div>
                        <span className="text-xs font-bold text-[#4E1225]">
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Coupon Box */}
                <div className="border border-[#E7D9BC] bg-[#FBF4E7]/60 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[#6E1B34]">
                      <Tag className="w-3.5 h-3.5 text-[#D9711F]" />
                      <span>Apply Coupon</span>
                    </div>
                    {availableCoupons.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowCouponsList(!showCouponsList)}
                        className="text-xs text-[#D9711F] font-semibold hover:underline"
                      >
                        {showCouponsList ? 'Hide Offers' : 'View Offers'}
                      </button>
                    )}
                  </div>

                  {couponApplied ? (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-emerald-800">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span>{couponApplied.code}</span>
                        <span className="text-emerald-600 font-normal">(-₹{discountAmount})</span>
                      </div>
                      <button
                        onClick={removeCoupon}
                        className="text-[11px] font-bold text-red-600 hover:text-red-800 uppercase"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <Input
                        placeholder="Coupon Code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="bg-white uppercase text-xs font-mono h-8 rounded-lg border-[#E7D9BC]"
                      />
                      <Button
                        type="button"
                        onClick={() => handleApplyCoupon()}
                        disabled={applyingCoupon || !couponCode.trim()}
                        className="bg-[#6E1B34] hover:bg-[#4E1225] text-white rounded-lg px-3 text-xs font-bold h-8"
                      >
                        {applyingCoupon ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}

                  {showCouponsList && availableCoupons.length > 0 && (
                    <div className="space-y-1.5 pt-1.5 border-t border-[#E7D9BC]/60">
                      {availableCoupons.map((c) => (
                        <div
                          key={c.code}
                          className="bg-white p-2 rounded-lg border border-[#E7D9BC] flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-bold text-[#6E1B34] font-mono bg-[#FBF4E7] px-1 py-0.5 rounded">
                              {c.code}
                            </span>
                            <p className="text-[#83725B] text-[10px] mt-0.5">{c.description || `Save with ${c.code}`}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleApplyCoupon(c.code)}
                            className="text-xs font-bold text-[#D9711F] hover:underline"
                          >
                            Apply
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-2 text-xs text-[#83725B]">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-bold text-[#2A1F16]">₹{cartTotal.toLocaleString()}</span>
                  </div>

                  {discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700 font-semibold">
                      <span>Coupon Discount</span>
                      <span>-₹{discountAmount.toLocaleString()}</span>
                    </div>
                  )}

                  {isGift && giftWrap && (
                    <div className="flex justify-between">
                      <span>Sacred Gift Packaging</span>
                      <span className="font-bold text-[#2A1F16]">₹{giftWrapFee.toLocaleString()}</span>
                    </div>
                  )}

                  {/* Selected Package Shipping Row */}
                  <div className="flex justify-between items-start">
                    <div>
                      <span>Delivery Package</span>
                      {selectedPackage && (
                        <p className="text-[10px] text-[#6E1B34] font-semibold">
                          {selectedPackage.name} ({selectedPackage.etd})
                        </p>
                      )}
                    </div>
                    <span className={shippingCost === 0 ? 'text-emerald-700 font-bold' : 'font-bold text-[#2A1F16]'}>
                      {shippingCost === 0 ? 'FREE' : `₹${shippingCost}`}
                    </span>
                  </div>

                  <div className="h-px bg-[#E7D9BC]/60 my-1.5"></div>

                  <div className="flex justify-between items-baseline pt-1">
                    <div>
                      <span className="font-bold text-[#4E1225] text-sm">Grand Total</span>
                      <p className="text-[10px] text-[#83725B]">All taxes & courier fees included</p>
                    </div>
                    <span className="font-price font-bold text-xl text-[#6E1B34]">
                      ₹{finalPayableTotal.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Submit / Pay Button */}
                <Button
                  type="submit"
                  form="checkout-form"
                  disabled={submitting}
                  className="w-full rounded-md bg-[#6E1B34] hover:bg-[#4E1225] text-white py-3 px-5 text-sm font-bold shadow-md hover:shadow-lg transition-all"
                  data-testid="place-order-btn"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </div>
                  ) : paymentMethod === 'cod' ? (
                    `Place COD Order (₹${finalPayableTotal.toLocaleString()})`
                  ) : (
                    `Pay ₹${finalPayableTotal.toLocaleString()} with Razorpay`
                  )}
                </Button>

                <div className="pt-1 text-center space-y-1 text-[#83725B] text-[11px]">
                  <div className="flex items-center justify-center gap-1.5">
                    <Lock className="w-3 h-3 text-[#2E7D45]" />
                    <span>256-Bit SSL Encrypted & Razorpay Protected</span>
                  </div>
                  <p className="text-[10px]">
                    Invoice & Shiprocket live tracking updates will be emailed immediately.
                  </p>
                </div>
              </motion.div>
            </div>

          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
